"use client";

import { useMemo, useState, type CSSProperties } from "react";
import { X, Plus, Check } from "lucide-react";
import { useTranslations } from "next-intl";
import { useGetUserQuery } from "@/api/auth/authPrivateApi";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { cn } from "@/src/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import type { DangerAction } from "./useDangerActions";
import { readRecentActions, rememberRecentAction } from "./recentDangerActions";

interface ChipCommentEditorProps {
  text: string;
  chips: string[];
  labelOf: (raw: string) => string;
  onTextChange: (value: string) => void;
  onRemoveChip: (index: number) => void;
  placeholder?: string;
  removeLabel?: string;
}

/** Поле «текст + чипы danger-действий»: чипы рендерятся внутри рамки поля. */
export function ChipCommentEditor({
  text,
  chips,
  labelOf,
  onTextChange,
  onRemoveChip,
  placeholder,
  removeLabel,
}: ChipCommentEditorProps) {
  return (
    <div className="rounded-md border border-input p-2 focus-within:border-ring">
      <Textarea
        value={text}
        onChange={(e) => onTextChange(e.target.value)}
        rows={3}
        placeholder={placeholder}
        className="resize-y w-full border-0 p-1 shadow-none focus-visible:ring-0 focus-visible:border-0"
        style={
          {
            fieldSizing: "fixed",
            maxWidth: "100%",
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          } as CSSProperties
        }
      />
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-1 pt-1.5">
          {chips.map((raw, i) => (
            <span
              key={`${raw}-${i}`}
              className="inline-flex items-center gap-1 rounded-md border border-[#E0E3F5] bg-[#F1F3FF] px-2 py-1 text-xs font-medium text-[#3730A3]"
            >
              {labelOf(raw)}
              <button
                type="button"
                onClick={() => onRemoveChip(i)}
                aria-label={removeLabel}
                className="text-[#6366F1] transition-colors hover:text-[#3730A3]"
              >
                <X className="size-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

interface DangerActionPickerProps {
  catalog: DangerAction[];
  selected: string[];
  onAdd: (raw: string) => void;
  disabled?: boolean;
  /** Простым списком, без деления на «недавние» и «все» (школьный перечень). */
  plainList?: boolean;
}

/**
 * Кнопка «+ Добавить признак» → поисковый дропдаун со всеми danger-действиями.
 * Свёрнут в один триггер (их ~20), список фильтруется и скроллится;
 * попап остаётся открытым, чтобы добавить несколько признаков подряд.
 */
export function DangerActionPicker({
  catalog,
  selected,
  onAdd,
  disabled,
  plainList = false,
}: DangerActionPickerProps) {
  const t = useTranslations();
  const isMobile = useIsMobile();
  const selectedSet = new Set(selected);
  const [mobileOpen, setMobileOpen] = useState(false);

  const { data: me } = useGetUserQuery();
  const owner = me?.id != null ? `${me.school_id ?? 0}:${me.id}` : null;

  // Читаем при открытии списка: на сервере localStorage нет, а так список ещё и свежий,
  // если проктор успел выбрать признаки в другой вкладке.
  const [recentRaws, setRecentRaws] = useState<string[]>([]);
  const loadRecent = () => setRecentRaws(readRecentActions(owner));

  const addAction = (raw: string) => {
    setRecentRaws(rememberRecentAction(owner, raw));
    onAdd(raw);
  };

  const recent = useMemo(
    () =>
      recentRaws.flatMap((raw) => {
        const action = catalog.find((item) => item.raw === raw);
        return action ? [action] : [];
      }),
    [recentRaws, catalog],
  );

  // Недавние не дублируем ниже: в общем списке остаётся всё остальное.
  const rest = useMemo(() => {
    const recentSet = new Set(recent.map((a) => a.raw));
    return catalog.filter((a) => !recentSet.has(a.raw));
  }, [catalog, recent]);

  if (catalog.length === 0) return null;

  const triggerButton = (onClick?: () => void, open = false) => (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border border-[#E5E5E5] bg-white px-3 py-2 text-sm text-foreground transition-colors hover:bg-gray-50",
        "disabled:pointer-events-none disabled:opacity-50",
        isMobile && "w-full justify-center",
      )}
    >
      {open ? (
        <X className="size-4 text-gray-500" />
      ) : (
        <Plus className="size-4 text-[#2563EB]" />
      )}
      {open ? t("verdict-close-danger-list") : t("verdict-add-danger-action")}
    </button>
  );

  const renderItem = (a: DangerAction, itemClassName?: string) => {
    const isSelected = selectedSet.has(a.raw);

    return (
      <CommandItem
        key={a.raw}
        value={a.name}
        onSelect={() => {
          if (!isSelected) addAction(a.raw);
        }}
        className={cn(
          "cursor-pointer",
          itemClassName,
          isSelected && "cursor-default opacity-50 data-[selected=true]:opacity-50",
        )}
      >
        {isSelected ? (
          <Check className="size-4 text-[#16A34A]" />
        ) : (
          <Plus className="size-4 text-[#2563EB]" />
        )}
        {a.name}
      </CommandItem>
    );
  };

  const list = (itemClassName?: string) => (
    <Command>
      {/* Поиск нужен длинному каталогу; над коротким школьным списком он только мешает. */}
      {!plainList && <CommandInput placeholder={t("placeholder-filter")} />}
      <CommandList className="max-h-64">
        <CommandEmpty>{t("label-nothing-found")}</CommandEmpty>

        {plainList && (
          <CommandGroup>{catalog.map((a) => renderItem(a, itemClassName))}</CommandGroup>
        )}

        {!plainList && recent.length > 0 && (
          <>
            <CommandGroup heading={t("label-global-search-recent")}>
              {recent.map((a) => renderItem(a, itemClassName))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {!plainList && (
          <CommandGroup heading={recent.length > 0 ? t("option-all") : undefined}>
            {rest.map((a) => renderItem(a, itemClassName))}
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  );

  // Мобилка: раскрываем поиск+список ПРЯМО в потоке (внутри полноэкранной модалки),
  // без поповера/оверлея — нет вложенного scroll-lock/фокуса, клавиатура не перекрывает.
  if (isMobile) {
    return (
      <div className="space-y-2">
        {triggerButton(() => {
          if (!mobileOpen) loadRecent();
          setMobileOpen((v) => !v);
        }, mobileOpen)}
        {mobileOpen && (
          <div className="overflow-hidden rounded-md border border-[#E5E5E5]">
            {list("py-2.5")}
          </div>
        )}
      </div>
    );
  }

  return (
    // modal: своя scroll-lock у попапа, иначе колесо мыши глотает react-remove-scroll
    // родительского Dialog и список danger-действий не скроллится.
    <Popover modal onOpenChange={(open) => open && loadRecent()}>
      <PopoverTrigger asChild>{triggerButton()}</PopoverTrigger>
      <PopoverContent align="start" sideOffset={4} className="w-72 p-0">
        {list()}
      </PopoverContent>
    </Popover>
  );
}
